import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { curatorService } from '../../services';
import { useUser } from '../../context/UserContext';
import { useDialog } from '../../context/DialogContext';
import CategorySelect from '../../components/CategorySelect';
import MobileEventSheet from '../../components/MobileEventSheet';
import { categoryLabel } from '../../constants/eventCategories';
import { AdminPageHeader, AreaBar, DataTable, DropChip, SearchField, useAdminShell } from '../../components/admin/shell';

const PAGE_SIZE = 20;
const SOURCES = [{ value: '', label: 'ALL' }, { value: 'authored', label: 'ON PORTAL' }, { value: 'scraped', label: 'SCRAPED' }];
/** Queue orderings. DISTANCE only means something once an area is chosen, so it is offered only then. */
const SORTS = [
  { value: 'date', label: 'DATE' }, { value: 'name', label: 'NAME' }, { value: 'distance', label: 'DISTANCE', needsArea: true },
  { value: 'price', label: 'PRICE' }, { value: 'category', label: 'CATEGORY' }, { value: 'heat', label: 'HEAT' },
  { value: 'status', label: 'STATUS' }, { value: 'host', label: 'HOST' },
];

/** "SAT MAR 14" in the event's own local clock. */
const dayWord = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '').toUpperCase();
};
const hasLocation = (e) => !!(e.regionName || e.locationLatitude);
// A curator judges a candidate on whether anyone is going. The cap is shown when the
// event set one; otherwise the accepted count stands on its own.
const guests = (e) => {
  if (e.acceptedCount == null) return '—';
  if (e.maxAttendees) return `${e.acceptedCount}/${e.maxAttendees}`;
  return e.acceptedCount === 0 && e.applicationCount ? `0 OF ${e.applicationCount}` : String(e.acceptedCount);
};

/**
 * EVENTS › PICK EVENTS — spec/sections.md §11. The queue behind the Featured Wall,
 * scoped by the shared AREA bar. FEATURE / UNFEATURE are `curation` · edit; a row
 * click previews the event in the same sheet the wall uses.
 */
export default function PickEventsPage() {
  const { can } = useUser();
  const { showAlert } = useDialog();
  const { area: areaCtl } = useAdminShell();
  const area = areaCtl?.area || null;
  const radiusMiles = areaCtl?.radiusMiles || 50;
  const canEdit = can('curation', 'edit');
  const [category, setCategory] = useState('');
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [source, setSource] = useState('');
  const [sort, setSort] = useState('date');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [previewCode, setPreviewCode] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setApplied(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  // Distance is meaningless without an area: fall back to date when the area is cleared.
  useEffect(() => { if (!area && sort === 'distance') setSort('date'); }, [area, sort]);
  useEffect(() => { setPage(1); }, [area, radiusMiles, category, source, sort, applied]);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await curatorService.getQueue({
      lat: area?.lat ?? null,
      lng: area?.lng ?? null,
      radiusMiles: area ? radiusMiles : null,
      origin: source || null,
      sort,
      category: category || null,
      q: applied || null,
    });
    setLoading(false);
    if (r.success) setQueue(r.data?.events || []);
    else showAlert({ title: 'ERROR', message: r.error || 'Failed to load the queue.', type: 'error', theme: 'admin' });
  }, [area, radiusMiles, source, sort, category, applied, showAlert]);
  useEffect(() => { load(); }, [load]);

  const feature = async (e) => {
    // No regionId on purpose: the server files the pick under the EVENT's own
    // region / coordinates, never the area the curator happened to be browsing.
    const r = await curatorService.featureEvent(e.id, { regionId: null, category: e.category || 'other' });
    if (r.success) load(); else showAlert({ title: 'ERROR', message: r.error || 'Feature failed.', type: 'error', theme: 'admin' });
  };
  const unfeature = async (e) => {
    const r = await curatorService.unfeatureEvent(e.id);
    if (r.success) load(); else showAlert({ title: 'ERROR', message: r.error || 'Unfeature failed.', type: 'error', theme: 'admin' });
  };

  const sortOptions = useMemo(() => SORTS.filter((o) => !o.needsArea || area), [area]);
  const pageRows = useMemo(() => queue.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [queue, page]);

  return (
    <>
      <AdminPageHeader section="pick" meta="The queue behind the Featured Wall." />
      <AreaBar note={(a) => (a.area ? `Everything within ${a.radiusMiles} mi. Featuring files the event under its own area.` : 'Everywhere. Search a city to narrow it down.')} />
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <button
              type="button"
              onClick={() => setCategoryOpen((o) => !o)}
              aria-haspopup="dialog"
              aria-expanded={categoryOpen}
              className="h-[38px] rounded-full bg-white px-4 font-mono text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-[#F2F2F2]"
            >
              CATEGORY: {category ? categoryLabel(category) : 'ALL'} {categoryOpen ? '▴' : '▾'}
            </button>
            {categoryOpen && (
              <div className="absolute left-0 top-[calc(100%+8px)] z-20 w-[320px] max-w-[calc(100vw-40px)] rounded-[22px] bg-white p-4 shadow-[0_12px_32px_rgba(0,0,0,.14)]">
                <CategorySelect
                  includeOther
                  selected={new Set(category ? [category] : [])}
                  onToggle={(slug) => { setCategory((cur) => (cur === slug ? '' : slug)); setCategoryOpen(false); }}
                  onClear={() => { setCategory(''); setCategoryOpen(false); }}
                />
              </div>
            )}
          </div>
          <DropChip label="SOURCE" value={source} onChange={setSource} options={SOURCES} />
          <DropChip label="SORT" value={sort} onChange={setSort} options={sortOptions} />
          <SearchField value={search} onChange={setSearch} placeholder="SEARCH" />
        </div>
        <DataTable
          columns={[
            { key: 'title', label: 'EVENT', fr: 2.2 },
            { key: 'hostName', label: 'HOST', fr: 1.4, render: (e) => e.hostName || '—' },
            { key: 'category', label: 'CATEGORY', fr: 1, render: (e) => categoryLabel(e.category) },
            { key: 'dateTime', label: 'DATE', fr: 0.9, render: (e) => dayWord(e.dateTime) },
            { key: 'guests', label: 'GUESTS', fr: 0.8, right: true, render: guests },
            { key: 'miles', label: 'DIST', fr: 0.7, right: true, render: (e) => (hasLocation(e) ? (typeof e.miles === 'number' ? `${e.miles.toFixed(0)} MI` : '—') : <span className="text-[#DC2626]">NO LOCATION</span>) },
          ]}
          rows={pageRows}
          loading={loading}
          onRowClick={(e) => { if (e.inviteCode) setPreviewCode(e.inviteCode); }}
          hasActions={canEdit}
          actions={(e) => {
            if (!canEdit) return [];
            if (e.isFeatured) return [{ label: 'UNFEATURE', onClick: () => unfeature(e) }];
            // No region and no coordinates means nowhere to file it — the button is absent, not disabled.
            if (!hasLocation(e)) return [];
            return [{ label: 'FEATURE', variant: 'solid', onClick: () => feature(e) }];
          }}
          total={queue.length}
          footerLeft={`${queue.length.toLocaleString()} eligible`}
          page={page}
          pageSize={PAGE_SIZE}
          onPage={setPage}
        />
      </div>
      {/* Preview — the same embedded EventApplyPage the featured wall uses. */}
      <MobileEventSheet code={previewCode} open={!!previewCode} onClose={() => setPreviewCode(null)} />
    </>
  );
}
